import { useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { useAuth } from '../../../hooks/useAuth'
import { ROUTES } from '../../../routes/types'
import { Avatar, Icon } from '../../atoms'

interface SettingsDrawerProps {
  isOpen: boolean
  onClose: () => void
}

export function SettingsDrawer({ isOpen, onClose }: SettingsDrawerProps) {
  const { user, signOut, updatePreferences } = useAuth()
  const navigate = useNavigate()
  const drawerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen, onClose])

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }

    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  const handleSignOut = async () => {
    onClose()
    await signOut()
    navigate(ROUTES.LOGIN)
  }

  const handleDarkModeToggle = () => {
    const isDark = user?.preferences.darkMode ?? false
    const next = !isDark

    updatePreferences({ darkMode: next })
    document.documentElement.classList.toggle('dark', next)
    // UX L3 — set color-scheme so user-agent widgets (native <select>
    // chevron, scrollbars, form controls) pick the OS dark palette. Pairs
    // with the same line in useAuth's useEffect so a manual toggle here
    // and a profile-driven sync there both end up consistent.
    document.documentElement.style.colorScheme = next ? 'dark' : 'light'
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className="absolute right-0 top-0 flex h-full w-[350px] animate-slide-right flex-col bg-white shadow-xl dark:bg-gray-800"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Avatar name={user?.name} size="md" />
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">{user?.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
          >
            <Icon name="close" size="sm" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto px-6 py-4">
          <nav className="space-y-1">
            <Link
              to={ROUTES.DASHBOARD}
              onClick={onClose}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Icon name="dashboard" size="sm" />
              Dashboard
            </Link>
            <Link
              to={ROUTES.PROFILE}
              onClick={onClose}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Icon name="user" size="sm" />
              Profile
            </Link>
          </nav>

          {/* Dark mode toggle */}
          <div className="mt-6 border-t border-gray-200 pt-4 dark:border-gray-700">
            <button
              onClick={handleDarkModeToggle}
              className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <span>Dark Mode</span>
              <div
                className={`relative h-6 w-11 rounded-full transition-colors ${user?.preferences.darkMode ? 'bg-primary-500' : 'bg-gray-300 dark:bg-gray-600'}`}
              >
                <div
                  className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${user?.preferences.darkMode ? 'translate-x-5' : 'translate-x-0.5'}`}
                />
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={handleSignOut}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  )
}
